import React from 'react';
import { Badge } from 'src/ui/Badge';
import { Button } from 'src/ui/Button';
import { Spinner } from 'src/ui/Spinner';
import { useToast } from 'src/ui/Toast';
import {
  useGetUserVectorProfileQuery,
  useGetUserVectorSuggestionsQuery,
  useSyncUserVectorProfileMutation,
} from 'src/store/vectorProfileApi';
import { MatchReach } from './matchReach';

interface UserVectorProfileProps {
  userId: string | null;
}

const SyncVectorButton = ({ userId }: { userId: string }) => {
  const push = useToast();
  const [sync, { isLoading }] = useSyncUserVectorProfileMutation();

  const handleSync = async () => {
    try {
      await sync(userId).unwrap();
      push({
        title: 'Vector profile rebuilt',
        message: 'Labels and embeddings were regenerated from the profile.',
        type: 'success',
      });
    } catch (e: any) {
      push({
        title: 'Sync failed',
        message: e?.data?.message || 'Could not rebuild the vector profile.',
        type: 'error',
      });
    }
  };

  return (
    <Button
      size="sm"
      variant="primary"
      onClick={handleSync}
      disabled={isLoading}
    >
      {isLoading ? 'Rebuilding…' : 'Create / Update Vector'}
    </Button>
  );
};

const pct = (n: number | null | undefined) =>
  n == null ? 'n/a' : `${Math.round(n * 100)}%`;

const MatchScores = ({ userId }: { userId: string }) => {
  const { data } = useGetUserVectorSuggestionsQuery(userId, { skip: !userId });
  const rows = data?.data ?? [];

  return (
    <div>
      <div
        style={{
          fontWeight: 600,
          fontSize: 'var(--fs-sm)',
          color: 'var(--text)',
          marginBottom: 12,
        }}
      >
        Recent Match Scores ({rows.length})
      </div>
      {rows.length === 0 ? (
        <span style={{ color: 'var(--text-faint)', fontSize: 'var(--fs-sm)' }}>
          No match suggestions scored yet.
        </span>
      ) : (
        <div className="col" style={{ gap: 8 }}>
          {rows.map((s) => {
            const b = s.breakdown;
            return (
              <div
                key={s.id}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 12,
                }}
              >
                <div
                  className="row"
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 4,
                  }}
                >
                  <span
                    className="mono"
                    style={{
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {s.candidateId.slice(0, 8)}…
                  </span>
                  <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                    {b?.gated && (
                      <Badge color="red" size="sm">
                        gated: {b.gateReasons.join(', ')}
                      </Badge>
                    )}
                    <Badge color={s.score >= 0.5 ? 'green' : 'gray'} size="sm">
                      {s.score.toFixed(2)}
                    </Badge>
                  </div>
                </div>
                {b && (
                  <div
                    className="row"
                    style={{
                      gap: 12,
                      flexWrap: 'wrap',
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    <span>
                      pref A→B {pct(b.prefSatAtoB)} · B→A {pct(b.prefSatBtoA)}
                    </span>
                    <span>homophily {pct(b.homophily)}</span>
                    <span>vibe {pct(b.vibe)}</span>
                    <span>geo {pct(b.geo)}</span>
                  </div>
                )}
                {s.reason && (
                  <div
                    style={{
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--text-muted)',
                      marginTop: 4,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {s.reason}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const weightColor = (w: number): string => {
  if (w >= 8) return 'var(--green-solid, #16a34a)';
  if (w >= 4) return 'var(--blue-solid, #2563eb)';
  if (w >= 1) return 'var(--text-secondary)';
  if (w <= -8) return 'var(--red-solid, #dc2626)';
  if (w <= -4) return 'var(--orange-solid, #ea580c)';
  return 'var(--text-muted)';
};

const weightBg = (w: number): string => {
  if (w >= 8) return 'var(--green-bg, #f0fdf4)';
  if (w >= 4) return 'var(--blue-bg, #eff6ff)';
  if (w >= 1) return 'var(--bg-canvas)';
  if (w <= -8) return 'var(--red-bg, #fef2f2)';
  if (w <= -4) return 'var(--orange-bg, #fff7ed)';
  return 'var(--bg-canvas)';
};

const UserVectorProfile = ({ userId }: UserVectorProfileProps) => {
  const { data, isLoading, error } = useGetUserVectorProfileQuery(
    userId ?? '',
    {
      skip: !userId,
    }
  );

  if (!userId) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <span style={{ color: 'var(--text-muted)' }}>
          Select a user to view their vector profile
        </span>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <div className="col" style={{ gap: 16, alignItems: 'center' }}>
          <span
            style={{
              color: 'var(--red-solid, #dc2626)',
              fontSize: 'var(--fs-sm)',
            }}
          >
            No vector profile found — rebuild it from the profile below.
          </span>
          <SyncVectorButton userId={userId} />
        </div>
      </div>
    );
  }

  const facts = Object.entries(data.hardFilters.facts ?? {});
  const prefs = Object.entries(data.hardFilters.preferences ?? {});
  const factLabels = data.labels.filter((l: any) => l.type === 'FACT');
  const prefLabels = data.labels.filter((l: any) => l.type === 'PREFERENCE');

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 24 }}>
      <div className="col" style={{ gap: 24 }}>
        {/* Header with rebuild action */}
        <div
          className="row"
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: 'var(--fs-lg)',
              color: 'var(--text)',
            }}
          >
            Vector Profile
          </span>
          <SyncVectorButton userId={userId} />
        </div>

        <hr
          style={{
            border: 'none',
            borderTop: '1px solid var(--border)',
            margin: 0,
          }}
        />

        {/* Embedding status — a single personality/vibe vector (Phase 5) */}
        <div>
          <div
            style={{
              fontWeight: 600,
              fontSize: 'var(--fs-sm)',
              color: 'var(--text)',
              marginBottom: 12,
            }}
          >
            Embedding
          </div>
          <Badge color={data.hasEmbeddings.personality ? 'green' : 'red'}>
            Personality embedding:{' '}
            {data.hasEmbeddings.personality ? '✓ ready' : '✗ missing'}
          </Badge>
        </div>

        <hr
          style={{
            border: 'none',
            borderTop: '1px solid var(--border)',
            margin: 0,
          }}
        />

        {/* Recent match scores with explainable breakdown */}
        <MatchScores userId={userId} />

        <hr
          style={{
            border: 'none',
            borderTop: '1px solid var(--border)',
            margin: 0,
          }}
        />

        {/* Match reach — dealbreakers vs. the candidate pool distribution */}
        <MatchReach userId={userId} />

        <hr
          style={{
            border: 'none',
            borderTop: '1px solid var(--border)',
            margin: 0,
          }}
        />

        {/* Hard filters */}
        <div>
          <div
            style={{
              fontWeight: 600,
              fontSize: 'var(--fs-sm)',
              color: 'var(--text)',
              marginBottom: 12,
            }}
          >
            Hard Filters
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
            }}
          >
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                padding: 16,
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  marginBottom: 8,
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text-secondary)',
                }}
              >
                FACTS (about this user)
              </div>
              {facts.length === 0 ? (
                <span
                  style={{
                    color: 'var(--text-faint)',
                    fontSize: 'var(--fs-sm)',
                  }}
                >
                  None
                </span>
              ) : (
                <div className="col" style={{ gap: 4 }}>
                  {facts.map(([k, v]) => (
                    <div
                      key={k}
                      className="row"
                      style={{ justifyContent: 'space-between' }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--fs-sm)',
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {k}
                      </span>
                      <code
                        style={{
                          fontSize: 'var(--fs-sm)',
                          background: 'var(--bg-canvas)',
                          padding: '1px 4px',
                          borderRadius: 3,
                          border: '1px solid var(--border-subtle)',
                        }}
                      >
                        {JSON.stringify(v)}
                      </code>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                padding: 16,
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  marginBottom: 8,
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text-secondary)',
                }}
              >
                PREFERENCES (what they want)
              </div>
              {prefs.length === 0 ? (
                <span
                  style={{
                    color: 'var(--text-faint)',
                    fontSize: 'var(--fs-sm)',
                  }}
                >
                  None
                </span>
              ) : (
                <div className="col" style={{ gap: 4 }}>
                  {prefs.map(([k, v]) => (
                    <div
                      key={k}
                      className="row"
                      style={{ justifyContent: 'space-between' }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--fs-sm)',
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {k}
                      </span>
                      <code
                        style={{
                          fontSize: 'var(--fs-sm)',
                          background: 'var(--bg-canvas)',
                          padding: '1px 4px',
                          borderRadius: 3,
                          border: '1px solid var(--border-subtle)',
                        }}
                      >
                        {JSON.stringify(v)}
                      </code>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <hr
          style={{
            border: 'none',
            borderTop: '1px solid var(--border)',
            margin: 0,
          }}
        />

        {/* Profile labels */}
        <div>
          <div
            style={{
              fontWeight: 600,
              fontSize: 'var(--fs-sm)',
              color: 'var(--text)',
              marginBottom: 12,
            }}
          >
            Taxonomy Labels ({data.labels.length} total)
          </div>

          {data.labels.length === 0 ? (
            <span
              style={{ color: 'var(--text-faint)', fontSize: 'var(--fs-sm)' }}
            >
              No labels — run vector migration or generate a match first.
            </span>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
              }}
            >
              {/* FACTS column */}
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    marginBottom: 8,
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--blue-solid, #2563eb)',
                  }}
                >
                  FACTS ({factLabels.length})
                </div>
                <div className="col" style={{ gap: 8 }}>
                  {factLabels.map((l: any) => (
                    <div
                      key={l.id}
                      style={{
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-md)',
                        padding: 12,
                        background: weightBg(l.weight),
                      }}
                    >
                      <div
                        className="row"
                        style={{
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'monospace',
                            fontSize: 'var(--fs-sm)',
                            fontWeight: 600,
                            color: weightColor(l.weight),
                            flex: 1,
                          }}
                        >
                          {l.labelKey}
                          {l.status === 'pending' && (
                            <span
                              style={{ color: 'var(--orange-solid, #ea580c)' }}
                            >
                              {' '}
                              ·pending
                            </span>
                          )}
                        </span>
                        <Badge
                          color={l.weight >= 0 ? 'green' : 'red'}
                          size="sm"
                        >
                          {l.weight > 0 ? `+${l.weight}` : l.weight}
                        </Badge>
                      </div>
                      {l.context && (
                        <div
                          style={{
                            fontSize: 'var(--fs-xs)',
                            color: 'var(--text-secondary)',
                            marginTop: 4,
                          }}
                        >
                          {l.context}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* PREFERENCES column */}
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    marginBottom: 8,
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--purple-solid, #7c3aed)',
                  }}
                >
                  PREFERENCES ({prefLabels.length})
                </div>
                <div className="col" style={{ gap: 8 }}>
                  {prefLabels.map((l: any) => (
                    <div
                      key={l.id}
                      style={{
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-md)',
                        padding: 12,
                        background: weightBg(l.weight),
                      }}
                    >
                      <div
                        className="row"
                        style={{
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'monospace',
                            fontSize: 'var(--fs-sm)',
                            fontWeight: 600,
                            color: weightColor(l.weight),
                            flex: 1,
                          }}
                        >
                          {l.labelKey}
                          {l.status === 'pending' && (
                            <span
                              style={{ color: 'var(--orange-solid, #ea580c)' }}
                            >
                              {' '}
                              ·pending
                            </span>
                          )}
                        </span>
                        <Badge
                          color={l.weight >= 0 ? 'green' : 'red'}
                          size="sm"
                        >
                          {l.weight > 0 ? `+${l.weight}` : l.weight}
                        </Badge>
                      </div>
                      {l.context && (
                        <div
                          style={{
                            fontSize: 'var(--fs-xs)',
                            color: 'var(--text-secondary)',
                            marginTop: 4,
                          }}
                        >
                          {l.context}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserVectorProfile;
